/**
 * Platform Dashboard Page
 *
 * WHY: This is the top-level landing page for the SPECTREIQ Platform.
 * It provides an aggregated view across ALL modules, showing:
 * - Platform-wide metrics and KPIs
 * - Quick access to active missions
 * - Status summaries from each deployed module
 * - User-customizable dashboard widgets
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { api } from '@/api'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Target,
  ArrowRight,
  Cpu,
  Scale,
  Package,
  FolderKanban,
  DollarSign,
  AlertTriangle,
  Clock,
} from 'lucide-react'

// Module cards configuration
const moduleCards = [
  {
    id: 'softwareiq',
    name: 'SoftwareIQ',
    description: 'Software assurance & vulnerability management',
    icon: Cpu,
    href: '/softwareiq',
    borderColor: 'border-blue-500/30',
    iconColor: 'text-blue-400',
    available: true,
  },
  {
    id: 'complyiq',
    name: 'ComplyIQ',
    description: 'Compliance management & audit tracking',
    icon: Scale,
    href: '/complyiq',
    borderColor: 'border-emerald-500/30',
    iconColor: 'text-emerald-400',
    available: true,
  },
  {
    id: 'supplyiq',
    name: 'SupplyIQ',
    description: 'Supply chain risk management',
    icon: Package,
    href: '/supplyiq',
    borderColor: 'border-purple-500/30',
    iconColor: 'text-purple-400',
    available: false,
  },
  {
    id: 'projectiq',
    name: 'ProjectIQ',
    description: 'Project & portfolio management',
    icon: FolderKanban,
    href: '/projectiq',
    borderColor: 'border-green-500/30',
    iconColor: 'text-green-400',
    available: false,
  },
  {
    id: 'financialiq',
    name: 'FinancialIQ',
    description: 'Financial analytics & reporting',
    icon: DollarSign,
    href: '/financialiq',
    borderColor: 'border-amber-500/30',
    iconColor: 'text-amber-400',
    available: false,
  },
]

export function PlatformDashboardPage() {
  const { user } = useAuth()

  // Fetch missions for platform-wide stats
  const { data: missions = [] } = useQuery({
    queryKey: ['platform', 'missions'],
    queryFn: () => api.platform.missions.list(),
  })

  // Fetch pending observations for cross-module awareness
  const { data: pendingObservations = [] } = useQuery({
    queryKey: ['softwareiq', 'observations', 'pending'],
    queryFn: () => api.softwareiq.observations.list({ status: 'pending' }),
  })

  const platformStats = {
    totalMissions: missions.length,
    activeMissions: missions.filter(m => m.status === 'active').length,
    completedMissions: missions.filter(m => m.status === 'completed').length,
    activeModules: moduleCards.filter(m => m.available).length,
    pendingActions: pendingObservations.length,
    criticalItems: pendingObservations.filter(o => o.severity === 'Critical').length,
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Header */}
      <div className="p-2">
        <div>
          <div className="flex items-start justify-between gap-6">
            <div>
              <h1 className="text-3xl font-display font-bold">
                Welcome back, {user?.firstName || user?.username || 'Operator'}
              </h1>
              <p className="mt-3 text-muted-foreground max-w-2xl">
                Your command center for software assurance, supply chain risk, and program management.
                You have <span className="text-foreground font-medium">{platformStats.activeMissions} active missions</span> and{' '}
                <span className="text-foreground font-medium">{platformStats.pendingActions} items</span> awaiting action.
              </p>
              <div className="mt-6 flex gap-4">
                <Button asChild variant="glow">
                  <Link to="/missions">
                    <Target className="mr-2 h-4 w-4" />
                    Manage Missions
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to="/softwareiq">
                    Enter SoftwareIQ
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Missions
            </CardTitle>
            <Target className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{platformStats.activeMissions}</div>
            <p className="text-xs text-muted-foreground mt-1">
              of {platformStats.totalMissions} total missions
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Pending Actions
            </CardTitle>
            <Clock className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{platformStats.pendingActions}</div>
            <p className="text-xs text-muted-foreground mt-1">
              items requiring attention
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Critical Items
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-400">{platformStats.criticalItems}</div>
            <p className="text-xs text-muted-foreground mt-1">
              high priority across modules
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Active Modules
            </CardTitle>
            <Scale className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{platformStats.activeModules}</div>
            <p className="text-xs text-muted-foreground mt-1">
              available modules
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Module Cards */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Platform Modules</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {moduleCards.filter(m => m.available).map((module) => {
            const ModuleIcon = module.icon

            return (
              <Card
                key={module.id}
                className={`transition-all duration-300 hover:shadow-lg hover:shadow-primary/5 cursor-pointer ${module.borderColor}`}
              >
                <CardHeader>
                  <div className={`p-2 rounded-lg bg-background/50 ${module.iconColor} w-fit`}>
                    <ModuleIcon className="h-6 w-6" />
                  </div>
                  <CardTitle className="mt-4">{module.name}</CardTitle>
                  <CardDescription>{module.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button asChild variant="ghost" className="w-full justify-between group">
                    <Link to={module.href}>
                      <span>Open Module</span>
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>

      {/* Recent Missions Quick View */}
      {missions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Recent Missions</h2>
            <Button asChild variant="ghost" size="sm">
              <Link to="/missions">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {missions.slice(0, 3).map((mission) => (
              <Card key={mission.mission_id} className="hover:border-primary/50 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">{mission.mission_name}</CardTitle>
                    <Badge
                      variant={mission.status === 'active' ? 'default' : 'secondary'}
                    >
                      {mission.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {mission.mission_objective || 'No description provided'}
                  </p>
                  <Button asChild variant="outline" size="sm" className="w-full">
                    <Link to={`/missions/${mission.mission_id}`}>
                      View Details
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
